import React from 'react';
import { FadeIn } from './FadeIn';
import { Button } from './Button';
import { CheckCircle2, Zap, Download, ShieldCheck } from 'lucide-react';
interface PricingCard {
  label?: string;
  title: string;
  subtitle: string;
  features: string[];
  extras?: string[];
  originalPrice?: string;
  price: string;
  priceLabel: string;
  buttonText: string;
  url: string;
  highlighted?: boolean;
}
export function PricingOptions() {
  const options: PricingCard[] = [{
    title: 'EMPIEZA DESDE CERO',
    subtitle: 'Aprende a vender en Amazon FBA paso a paso',
    features: ['Ideal si estás comenzando', 'Aprende cómo funciona Amazon Seller', 'Encuentra productos ganadores', 'Publica y realiza tus primeras ventas'],
    price: '$14.99',
    priceLabel: 'HOY SOLO',
    buttonText: 'QUIERO EMPEZAR DESDE CERO',
    url: 'https://43skvt-1y.myshopify.com/products/como-vender-en-amazon-fba-desde-cero-2026-bonos-exclusivos'
  }, {
    label: '⭐ MÁS ELEGIDO',
    title: 'SISTEMA COMPLETO AMAZON FBA',
    subtitle: 'De principiante a dueño de marca',
    features: ['Cómo vender en Amazon desde cero', 'Cómo encontrar productos rentables', 'Cómo trabajar con proveedores en China', 'Cómo negociar mejores precios', 'Cómo crear tu marca profesional', 'Cómo lanzar y escalar tu negocio'],
    extras: ['📘 Cómo vender en Amazon FBA desde cero', '📘 Crea tu marca en Amazon desde China'],
    originalPrice: '$39.98',
    price: '$29.99',
    priceLabel: 'HOY SOLO',
    buttonText: 'QUIERO EL SISTEMA COMPLETO',
    url: 'https://43skvt-1y.myshopify.com/products/sistema-completo-amazon-fba',
    highlighted: true
  }, {
    title: 'CREA TU PROPIA MARCA',
    subtitle: 'Guía Premium: Desde China a Amazon FBA',
    features: ['Encuentra proveedores verificables', 'Negocia precios inteligentes', 'Diseña tu marca y logo', 'Lanza tu producto con identidad propia'],
    price: '$24.99',
    priceLabel: 'HOY SOLO',
    buttonText: 'QUIERO CREAR MI MARCA',
    url: 'https://43skvt-1y.myshopify.com/products/crea-tu-marca-en-amazon-desde-china'
  }];
  return <section className="bg-brand-beige py-24 px-6" data-id="element-316">
      <div className="max-w-6xl mx-auto" data-id="element-317">
        {/* Header */}
        <FadeIn data-id="element-318">
          <div className="text-center mb-6" data-id="element-319">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-brand-green mb-4" data-id="element-320">
              ELIGE TU MEJOR OPCIÓN PARA EMPEZAR HOY
            </h2>
            <p className="text-lg md:text-xl text-brand-text/70 max-w-3xl mx-auto mb-2" data-id="element-321">
              No es solo aprender a vender… Es decidir hasta dónde quieres
              llegar.
            </p>
            <p className="text-lg md:text-xl text-brand-text/70 max-w-3xl mx-auto" data-id="element-322">
              Empieza desde cero o avanza directo a crear tu propia marca en
              Amazon y construir un negocio real.
            </p>
          </div>
        </FadeIn>

        {/* Cards grid: on desktop Option1 | Option3(highlighted) | Option2 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-5 items-stretch mt-14" data-id="element-323">
          {options.map((option, index) => <FadeIn key={index} delay={0.15 * index} data-id="element-324">
              <div className={`relative rounded-2xl p-8 h-full flex flex-col ${option.highlighted ? 'bg-brand-green text-white shadow-2xl border-2 border-brand-gold lg:scale-105 lg:-my-4 z-10' : 'bg-white text-brand-text shadow-sm border border-brand-beige'}`} data-id="element-325">
                {/* Badge */}
                {option.label && <div className="absolute -top-4 left-1/2 -translate-x-1/2" data-id="element-326">
                    <span className="bg-brand-gold text-brand-green font-bold text-sm px-5 py-1.5 rounded-full whitespace-nowrap shadow-lg" data-id="element-327">
                      {option.label}
                    </span>
                  </div>}

                {/* Title */}
                <h3 className={`text-xl md:text-2xl font-bold mb-2 mt-2 ${option.highlighted ? 'text-white' : 'text-brand-green'}`} data-id="element-328">
                  {option.title}
                </h3>
                <p className={`text-base mb-6 ${option.highlighted ? 'text-white/70' : 'text-brand-text/60'}`} data-id="element-329">
                  {option.subtitle}
                </p>

                {/* Divider */}
                <div className={`h-px mb-6 ${option.highlighted ? 'bg-white/20' : 'bg-brand-beige'}`} data-id="element-330" />

                {/* Features */}
                <div className="space-y-3 mb-6 flex-1" data-id="element-331">
                  {option.features.map((feature, i) => <div key={i} className="flex items-start gap-3" data-id="element-332">
                      <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${option.highlighted ? 'text-brand-gold' : 'text-brand-green'}`} data-id="element-333" />
                      <span className={`text-sm md:text-base font-medium ${option.highlighted ? 'text-white/90' : 'text-brand-text/80'}`} data-id="element-334">
                        {feature}
                      </span>
                    </div>)}
                </div>

                {/* Extras (bundle only) */}
                {option.extras && <div className={`rounded-xl p-4 mb-6 ${option.highlighted ? 'bg-white/10 border border-brand-gold/30' : 'bg-brand-beige/50'}`} data-id="element-335">
                    <p className={`text-sm font-bold mb-3 ${option.highlighted ? 'text-brand-gold' : 'text-brand-green'}`} data-id="element-336">
                      💥 ACCESO COMPLETO A LAS 2 GUÍAS
                    </p>
                    {option.extras.map((extra, i) => <p key={i} className={`text-sm mb-1 ${option.highlighted ? 'text-white/80' : 'text-brand-text/70'}`} data-id="element-337">
                        {extra}
                      </p>)}
                  </div>}

                {/* Price */}
                <div className="text-center mb-6" data-id="element-338">
                  {option.originalPrice && <p className={`text-sm font-medium mb-1 ${option.highlighted ? 'text-white/40' : 'text-brand-text/40'}`} data-id="element-339">
                      <span className="line-through" data-id="element-340">
                        OFERTA ESPECIAL {option.originalPrice}
                      </span>
                    </p>}
                  <p className={`text-xs font-semibold uppercase tracking-wider mb-1 ${option.highlighted ? 'text-brand-gold' : 'text-brand-text/50'}`} data-id="element-341">
                    💰 {option.priceLabel}
                  </p>
                  <p className={`text-5xl font-bold ${option.highlighted ? 'text-white' : 'text-brand-green'}`} data-id="element-342">
                    {option.price}
                  </p>
                </div>

                {/* Button */}
                <div className="mt-auto" data-id="element-343">
                  {option.highlighted ? <Button onClick={() => window.open(option.url, '_blank')} className="w-full bg-brand-gold hover:bg-brand-gold/90 text-brand-green text-lg py-5 shadow-xl" data-id="element-344">
                      {option.buttonText}
                    </Button> : <Button onClick={() => window.open(option.url, '_blank')} className="w-full text-base py-4" data-id="element-345">
                      {option.buttonText}
                    </Button>}

                  {/* Trust badges */}
                  <div className={`flex flex-col sm:flex-row items-center justify-center gap-3 text-xs mt-4 ${option.highlighted ? 'text-white/40' : 'text-brand-text/40'}`} data-id="element-346">
                    <span className="flex items-center gap-1.5" data-id="element-347">
                      <Zap className={`w-3.5 h-3.5 ${option.highlighted ? 'text-brand-gold' : 'text-brand-green'}`} data-id="element-348" />
                      ACCESO INMEDIATO
                    </span>
                    <span className="flex items-center gap-1.5" data-id="element-349">
                      <Download className={`w-3.5 h-3.5 ${option.highlighted ? 'text-brand-gold' : 'text-brand-green'}`} data-id="element-350" />
                      DESCARGA DIGITAL
                    </span>
                    <span className="flex items-center gap-1.5" data-id="element-351">
                      <ShieldCheck className={`w-3.5 h-3.5 ${option.highlighted ? 'text-brand-gold' : 'text-brand-green'}`} data-id="element-352" />
                      COMPRA SEGURA
                    </span>
                  </div>
                </div>
              </div>
            </FadeIn>)}
        </div>

        {/* Bottom text */}
        <FadeIn delay={0.5} data-id="element-353">
          <div className="text-center mt-12 space-y-3" data-id="element-354">
            <p className="text-lg text-brand-green font-semibold" data-id="element-355">
              La mayoría de nuestros estudiantes elige el sistema completo
              porque les permite empezar y escalar más rápido sin perder tiempo.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-brand-text/50 text-sm mt-4" data-id="element-356">
              <span className="flex items-center gap-2" data-id="element-357">
                <Zap className="w-4 h-4 text-brand-green" data-id="element-358" />
                Acceso inmediato después del pago
              </span>
              <span className="flex items-center gap-2" data-id="element-359">
                <Download className="w-4 h-4 text-brand-green" data-id="element-360" />
                Contenido digital disponible al instante
              </span>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}